// Auth client. credentials:'include' so the HttpOnly session cookie is sent —
// works same-origin (dev vite proxy) and cross-origin (prod, with CORS creds).

const API_BASE = import.meta.env.VITE_API_URL ?? ''

export interface AuthUser {
  username: string
  name: string
  role: 'admin' | 'supplier'
  manufacturer: string | null // the supplier this seat is locked to (role 'supplier')
  manufacturer_label?: string // display only; never used as the authorization scope
  must_change?: boolean // admin-issued temp password → the seat must set its own on first login
}

// Fired when any API call sees a 401 mid-session (e.g. the session expired). App
// listens and drops back to the login screen — so an expired session shows the gate,
// not a wall of "kunde inte hämta"-errors. Decoupled via a window event so data
// modules needn't know about App state.
export const UNAUTHORIZED_EVENT = 'mp:unauthorized'
export function flagUnauthorized(): void {
  if (typeof window !== 'undefined') window.dispatchEvent(new Event(UNAUTHORIZED_EVENT))
}

async function readUser(response: Response): Promise<AuthUser> {
  const data = await response.json()
  const user = data?.user
  if (!user || typeof user.username !== 'string' || !user.username.trim()
    || typeof user.name !== 'string' || !['admin', 'supplier'].includes(user.role)
    || (user.manufacturer !== null && typeof user.manufacturer !== 'string')
    || (user.role === 'supplier' && !user.manufacturer?.trim())
    || (user.manufacturer_label !== undefined && typeof user.manufacturer_label !== 'string')
    || (user.must_change !== undefined && typeof user.must_change !== 'boolean')) {
    throw new Error('The sign-in response could not be read. Please try again.')
  }
  return user as AuthUser
}

export async function me(): Promise<AuthUser | null> {
  const response = await fetch(`${API_BASE}/api/me`, { credentials: 'include' })
  if (response.status === 401) return null
  if (!response.ok) throw new Error(`Could not verify the session (HTTP ${response.status}).`)
  return readUser(response)
}

export async function login(username: string, password: string): Promise<AuthUser> {
  const r = await fetch(`${API_BASE}/api/login`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ username, password }),
  })
  if (!r.ok) {
    const detail = await r.json().then((d) => d.detail).catch(() => null)
    throw new Error(detail || 'Sign-in failed. Please try again.')
  }
  return readUser(r)
}

export async function changePassword(currentPassword: string, newPassword: string): Promise<AuthUser> {
  const r = await fetch(`${API_BASE}/api/change-password`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
  })
  if (r.status === 401) flagUnauthorized()
  if (!r.ok) {
    const detail = await r.json().then((d) => d.detail).catch(() => null)
    throw new Error(detail || 'Could not change the password.')
  }
  return readUser(r)
}

export async function logout(): Promise<void> {
  const response = await fetch(`${API_BASE}/api/logout`, { method: 'POST', credentials: 'include' })
  if (!response.ok && response.status !== 401) throw new Error('Could not sign out. Please try again.')
}
